import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { TabBar, Button, useToast } from '@chikara/ui';
import type { MultiSelectOption } from '@chikara/ui';
import { downloadBlob } from '@chikara/utils';
import { ActivateCardModal } from '@/features/card/components/ActivateCardModal';
import { PageContent } from '@/shared/components/PageContent';
import { CardAssignedTable } from '@/features/card/components/CardAssignedTable/CardAssignedTable';
import { CardBatchesView } from '@/features/card/components/CardBatchesView/CardBatchesView';
import { CardFilters } from '@/features/card/components/CardFilters/CardFilters';
import { CardKpisSummary, type CardKpiItem } from '@/features/card/components/CardKpisSummary/CardKpisSummary';
import { useCards } from '@/features/card/hooks/useCard/useCards';
import { useCardKpis } from '@/features/card/hooks/useCard/useCardKpis';
import { cardService } from '@/features/card/services/cardService/cardService';
import { buildCardListParams } from '@/features/card/utils/cardFilters.utils';
import { cardStatusLabel } from '@/features/card/utils/cardStatusLabels';
import { buildCardExportDirective, CARD_PDF_FILENAME, CARD_XLSX_FILENAME } from '@/features/card/utils/cardExport.utils';
import type { ExportFormat } from '@/shared/export/types';
import { buildExportIdentity } from '@/shared/export/identity';

import { CARD_STATUS_FILTERS, CARD_TYPE_FILTERS } from '@/features/card/constants/cardFilters.constants';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAccounts } from '@/features/card/hooks/useCard/useAccounts';
import type { CardFilters as CardFiltersState } from '@/features/card/types/card.types';

import styles from './CardPage.module.css';
import { t } from '@/i18n';


export default function CardPage() {
  const toast = useToast();
  const { userData, activeCompany, payload } = useAuth();
  const basic = isBasic(userData.role);
  const [activeTab, setActiveTab] = useState('asigned');
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);

  // Estado de filtros: borrador (controles) vs aplicado (consulta).
  const [draftFilters, setDraftFilters] = useState<CardFiltersState>({});
  const [appliedFilters, setAppliedFilters] = useState<CardFiltersState>({});

  const location = useLocation();
  const tab = location.state?.tab;

  const { cards, catalogs, pagination: cardsPagination, refetch: refetchCards, isSpinnerLoading: isCardsLoading } = useCards(appliedFilters);
  const { accounts, pagination: accountsPagination, refetch: refetchAccounts, isSpinnerLoading: isAccountsLoading } = useAccounts();
  const { kpis } = useCardKpis(!basic);

  const isPendingStatus = (status: string) => {
    const s = status?.toString().toLowerCase();
    return s === 'pending_approval' || s === 'pending';
  };

  // Catálogos de los filtros. Seam BF-623: hoy salen de constantes; cuando el BE
  // exponga el catálogo, se sustituye la fuente sin tocar el componente.
  const statusValues = useMemo(
    () => (catalogs.statuses.length > 0 ? catalogs.statuses : CARD_STATUS_FILTERS.map((option) => option.value)),
    [catalogs.statuses],
  );
  const statusOptions = useMemo(
    () => statusValues.map((value) => ({
      value,
      label: cardStatusLabel(value),
    })),
    [statusValues],
  );
  const typeOptions = useMemo(
    () => CARD_TYPE_FILTERS.map((option) => ({ value: option.value, label: t(option.labelKey) })),
    [],
  );
  // Etiquetas: catálogo derivado de las tarjetas cargadas (seam BF-623).
  const labelOptions = useMemo<MultiSelectOption[]>(() => {
    if (catalogs.tags.length > 0) {
      return catalogs.tags.map((tag) => ({ value: tag, label: tag }));
    }
    // Fallback mientras el catálogo del BE no esté desplegado.
    const seen = new Set<string>();
    cards.forEach((card) => {
      const tag = card.tag;
      if (tag && tag !== 'Sin Asignar') seen.add(tag);
    });
    return Array.from(seen).map((tag) => ({ value: tag, label: tag }));
  }, [catalogs.tags, cards]);

  const handleFilterChange = useCallback((patch: Partial<CardFiltersState>) => {
    setDraftFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
  }, [draftFilters]);

  const handleClearFilters = useCallback(() => {
    setDraftFilters({});
    setAppliedFilters({});
  }, []);

  // CA8: la card "Sin límite asignado" es clickeable y aplica ese filtro.
  const handleApplyNoLimit = useCallback(() => {
    setDraftFilters((prev) => ({ ...prev, noLimit: true }));
    setAppliedFilters((prev) => ({ ...prev, noLimit: true }));
  }, []);

  const kpiItems: CardKpiItem[] = useMemo(
    () => [
      { key: 'active', label: t('dashboard.card.kpis.active'), count: kpis?.active ?? 0, color: 'success' },
      { key: 'blocked', label: t('dashboard.card.kpis.blocked'), count: kpis?.blocked ?? 0, color: 'error' },
      { key: 'inactive', label: t('dashboard.card.kpis.inactive'), count: kpis?.inactive ?? 0, color: 'muted' },
      {
        key: 'noLimit',
        label: t('dashboard.card.kpis.noLimit'),
        count: kpis?.noLimit ?? 0,
        color: 'warning',
        onClick: handleApplyNoLimit,
      },
    ],
    [kpis, handleApplyNoLimit],
  );

  const buildCardDirective = (format: ExportFormat) =>
    buildCardExportDirective(
      {
        filters: appliedFilters,
        ...buildExportIdentity({
          companyName: activeCompany?.company_name,
          keyId: payload?.customer_id,
          customerUuid: payload?.customer_uuid ?? activeCompany?.customer_uuid,
        }),
      },
      format
    );

  const buildCardExportRequest = () => ({
    ...buildCardListParams(appliedFilters),
    page: 1,
    limit: cardsPagination.totalItems || 1,
    balance: true,
  });

  const handleExportXlsx = useCallback(async () => {
    try {
      const { blob, filename } = await cardService.exportCards(
        buildCardExportRequest(),
        buildCardDirective('xlsx')
      );
      downloadBlob(blob, filename ?? CARD_XLSX_FILENAME);
    } catch {
      toast.error(t('export.errors.download'));
    }
  }, [appliedFilters, cardsPagination.totalItems, activeCompany, payload, toast]);

  const handleExportPdf = useCallback(async () => {
    try {
      const { blob, filename } = await cardService.exportCards(
        buildCardExportRequest(),
        buildCardDirective('pdf')
      );
      downloadBlob(blob, filename ?? CARD_PDF_FILENAME);
    } catch {
      toast.error(t('export.errors.download'));
    }
  }, [appliedFilters, cardsPagination.totalItems, activeCompany, payload, toast]);

  const tabs = [
    {
      label: t('dashboard.card.tabs.assigned'),
      value: 'asigned',
    },
    ...(!basic ? [
      {
        label: activeTab === 'pending_approval' && accountsPagination.totalItems > 0 && !isAccountsLoading && !isCardsLoading
          ? `${t('dashboard.card.tabs.pending')} (${accountsPagination.totalItems})`
          : t('dashboard.card.tabs.pending'),
        value: 'pending_approval',
      },
      {
        label: t('dashboard.card.tabs.tracking'),
        value: 'traking',
      },
    ] : [])
  ];

  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
  }, [tab]);

  // Las tarjetas asignadas las carga el hook (y se recargan al aplicar filtros);
  // aquí solo se refresca la pestaña de pendientes.
  useEffect(() => {
    if (activeTab === 'pending_approval') {
      refetchAccounts(1, 10, 'pending_approval');
    }
  }, [activeTab, refetchAccounts]);

  const displayedData = activeTab === 'pending_approval' ? accounts : (
    basic ? cards : cards.filter(card => !isPendingStatus(card.status))
  );

  const currentPagination = activeTab === 'pending_approval' ? accountsPagination : cardsPagination;
  const currentRefetch = activeTab === 'pending_approval' ? refetchAccounts : refetchCards;

  return (
    <PageContent 
      title={t('dashboard.card.title')}
    >
      {!basic && (
        <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      )}

      {(activeTab === 'asigned' || activeTab === 'pending_approval') && (
        <div className={styles.content}>
          {activeTab === 'asigned' && !basic && (
            <CardKpisSummary
              title={t('dashboard.card.kpis.title')}
              items={kpiItems}
              isLoading={!kpis}
            />
          )}

          {activeTab === 'asigned' && !basic && (
            <CardFilters
              statusOptions={statusOptions}
              typeOptions={typeOptions}
              labelOptions={labelOptions}
              draftFilters={draftFilters}
              onFilterChange={handleFilterChange}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
              hasResults={cards.length > 0}
              onExportExcel={handleExportXlsx}
              onExportPdf={handleExportPdf}
            />
          )}

          {cards.length > 0 && activeTab === 'asigned' && (
            <div className={styles.buttonContainer}>
              <Button
                variant="outline"
                size="md"
                onClick={() => setIsActivateModalOpen(true)}
              >
                {t('dashboard.card.actions.activate')}
              </Button>
            </div>
          )}

          <CardAssignedTable
            data={displayedData}
            onActivateCard={() => setIsActivateModalOpen(true)}
            onRefetch={currentRefetch}
            pagination={currentPagination}
            basic={basic}
          />
        </div>
      )}

      {activeTab === 'traking' && (
        <div className={styles.content}>
          <CardBatchesView />
        </div>
      )}

      <ActivateCardModal
        isOpen={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        onSuccess={() => {
          currentRefetch(1, 10, undefined);
          setIsActivateModalOpen(false);
        }}
      />
    </PageContent>
  );
}

export { CardPage };
